import { useState } from 'react';
import { useContacts } from '../hooks/useContacts';
import Badge from '../components/ui/Badge';

const APP_ORIGIN = window.location.origin;

function generateBookmarklet(trackingCode) {
  const apiUrl = APP_ORIGIN + '/api/track/' + trackingCode;
  const scriptUrl = APP_ORIGIN + '/ko-tracker.js';

  const code = `javascript:void(function(){window.__KO_API='${apiUrl}';window.__KO_CODE='${trackingCode}';if(window.__KO_ACTIVE){window.__KO_CLEANUP();return;}var s=document.createElement('script');s.src='${scriptUrl}?v='+Date.now();document.head.appendChild(s);}())`;

  return code;
}

export default function Bookmarklet() {
  const { data: contactsData } = useContacts();
  const contacts = (contactsData?.data || []).filter(c => c.tracking_enabled && c.tracking_code);
  const [selected, setSelected] = useState(null);

  const selectedContact = contacts.find(c => c.id === selected);
  const bookmarkletUrl = selectedContact ? generateBookmarklet(selectedContact.tracking_code) : null;

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-[23px] font-bold mb-0.5">Conversion Bookmarklet</h1>
        <p className="text-gray-500 text-[13.5px]">Conversions visuell tracken — Element auf der Website auswählen und als Conversion-Ziel markieren</p>
      </div>

      <div className="grid grid-cols-[1fr_320px] gap-4">
        <div className="space-y-4">
          {/* Kunde wählen */}
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <h3 className="font-semibold text-[14.5px] mb-3">1. Kunde wählen</h3>
            {contacts.length === 0 ? (
              <p className="text-gray-400 text-[13px]">Keine Kunden mit aktivem Tracking gefunden. Aktiviere zuerst das Tracking bei einem Kunden.</p>
            ) : (
              <div className="space-y-2">
                {contacts.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setSelected(c.id)}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-lg border transition-all text-left ${
                      selected === c.id ? 'border-teal bg-teal-light' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="text-[13px] font-medium">{c.company || c.name}</div>
                      <div className="text-[11px] text-gray-500">{c.website || '—'} · Code: {c.tracking_code}</div>
                    </div>
                    {selected === c.id && <Badge bg="#E6F4F2" color="#0C9488">Ausgewählt</Badge>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Bookmarklet installieren */}
          {bookmarkletUrl && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
              <h3 className="font-semibold text-[14.5px] mb-3">2. Bookmarklet in Lesezeichen ziehen</h3>
              <p className="text-[13px] text-gray-500 mb-4">
                Ziehe den Button in deine <strong>Lesezeichen-Leiste</strong>.
              </p>

              <div className="bg-sidebar rounded-xl p-6 text-center mb-4">
                <a
                  href={bookmarkletUrl}
                  onClick={(e) => e.preventDefault()}
                  className="inline-block bg-teal text-white px-6 py-3 rounded-lg font-semibold text-[14px] cursor-grab active:cursor-grabbing shadow-lg hover:shadow-xl transition-shadow select-none"
                >
                  KO: {selectedContact.company || selectedContact.name}
                </a>
                <p className="text-white/60 text-[11px] mt-3">Diesen Button in die Lesezeichen-Leiste ziehen</p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-amber-800">
                <strong>Lesezeichen-Leiste nicht sichtbar?</strong> Strg+Shift+B (Windows) oder Cmd+Shift+B (Mac)
              </div>
            </div>
          )}

          {/* Anleitung */}
          {bookmarkletUrl && (
            <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
              <h3 className="font-semibold text-[14.5px] mb-3">3. Auf der Kunden-Website verwenden</h3>
              <ol className="space-y-3 text-[13px] text-gray-600 list-decimal list-inside">
                <li>Öffne die Website von <strong>{selectedContact.company || selectedContact.name}</strong></li>
                <li>Klicke das Bookmarklet in deiner Lesezeichen-Leiste</li>
                <li>Oben erscheint eine <strong>Toolbar</strong> — du bist jetzt im Auswahl-Modus</li>
                <li>Bewege die Maus über die Seite — Elemente werden <span className="text-teal font-medium">grün hervorgehoben</span></li>
                <li>Klicke auf das gewünschte Element (Button, Formular, Link, etc.)</li>
                <li>Wähle ein <strong>Conversion-Label</strong> (z.B. "form_submit") und optional eine Notiz</li>
                <li>Klicke <strong>"Conversion tracken"</strong> — fertig!</li>
              </ol>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <h3 className="font-semibold text-[14.5px] mb-3">Features</h3>
            <div className="space-y-2.5 text-[13px] text-gray-600">
              <div className="flex gap-2.5 items-start">
                <span className="text-teal mt-0.5">&#10003;</span>
                <span><strong>Visueller Element-Picker</strong> — Hover-Highlight wie in Elementor</span>
              </div>
              <div className="flex gap-2.5 items-start">
                <span className="text-teal mt-0.5">&#10003;</span>
                <span><strong>Auto-Label</strong> — erkennt automatisch ob Button, Formular, Link, etc.</span>
              </div>
              <div className="flex gap-2.5 items-start">
                <span className="text-teal mt-0.5">&#10003;</span>
                <span><strong>CSS-Selector</strong> — zeigt den technischen Pfad zum Element</span>
              </div>
              <div className="flex gap-2.5 items-start">
                <span className="text-teal mt-0.5">&#10003;</span>
                <span><strong>Notizen</strong> — optional beschreiben was das Element ist</span>
              </div>
              <div className="flex gap-2.5 items-start">
                <span className="text-teal mt-0.5">&#10003;</span>
                <span><strong>ESC zum Beenden</strong> — jederzeit abbrechen</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <h3 className="font-semibold text-[14.5px] mb-3">Häufige Labels</h3>
            <div className="flex flex-wrap gap-1.5">
              {['form_submit', 'cta_click', 'newsletter', 'download', 'phone_click', 'chat_start', 'add_to_cart', 'checkout', 'signup', 'demo_request', 'video_play', 'scroll_end'].map((l) => (
                <Badge key={l} bg="#F3F4F6" color="#6B7280">{l}</Badge>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
            <h3 className="font-semibold text-[14.5px] mb-3">Wo sehe ich die Daten?</h3>
            <p className="text-[13px] text-gray-500 leading-relaxed">
              Alle getrackten Conversions erscheinen im <strong>Tracking-Dashboard</strong> des jeweiligen Kunden unter dem Tab "Tracking".
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
